import { type QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { z } from 'zod/v4-mini';

import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { initializeI18n } from '$/components/core/Localization/18n';
import { useLocalizationStore } from '$/components/core/Localization/hooks/useLocalizationStore';
import { LocalizationModal } from '$/components/core/Localization/LocalizationModal';
import { setPageViewInformation } from '$/logger';
import { LoginPage } from '$/pages/Authentication/pages/LoginPage';
import { ErrorPage } from '$/pages/ErrorPage';
import { NotFoundPage } from '$/pages/NotFoundPage';
import { validateToken } from '$/services/usecases/authentication';
import { projectsQuery } from '$/services/usecases/projects/queries';
import { useFavoriteStore } from '$/stores/useFavoriteStore';
import { usePiwikStore } from '$/stores/usePiwikStore';
import { useProjectStore } from '$/stores/useProjectStore';

interface RouterContext {
  client: QueryClient;
}

const searchParameterToRemoveAfterUsage = [
  'brand',
  'register',
  'registerSchool',
];

const globalSearchSchema = z.object({
  keyMode: z.optional(z.boolean()),
  brand: z.optional(z.string()),
  register: z.optional(z.boolean()),
  registerSchool: z.optional(z.boolean()),
  country: z.optional(z.string()),
  language: z.optional(z.string()),
});

let isInitialNavigation = true;

export const RootRoute = createRootRouteWithContext<RouterContext>()({
  notFoundComponent: NotFoundPage,
  beforeLoad: async ({ location, context: { client }, search }) => {
    if (isInitialNavigation) {
      if (search.brand) {
        useLocalizationStore.setState({ brand: search.brand });
      }

      if (search.register) {
        useAuthenticationStore.getState().onModalOpen('register');
      }

      if (search.registerSchool) {
        useAuthenticationStore.getState().onModalOpen('registerSchool');
      }

      await validateToken();
      await initializeI18n(search);

      const { user } = useAuthenticationStore.getState();

      if (user?.id != null) {
        const [projects] = await Promise.all([
          client.ensureQueryData(projectsQuery()),
          useFavoriteStore.getState().getFavorites(),
        ]);
        useProjectStore.setState({
          projects: projects?.response.payload ?? [],
        });
      }

      usePiwikStore.setState({
        design: null,
        designName: null,
        designOrigin: null,
      });

      isInitialNavigation = false;
    }

    await setPageViewInformation(location.pathname, search);
  },
  component: () => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const username = import.meta.env.VITE_LOGIN_USER;
    const password = import.meta.env.VITE_LOGIN_PASSWORD;

    const loginEnabled =
      (username?.length ?? 0) > 0 &&
      (password?.length ?? 0) > 0 &&
      !new URLSearchParams(window.location.search).has('isWebview');

    if (loginEnabled && !isAuthenticated) {
      return <LoginPage />;
    }

    return (
      <>
        <Outlet />
        <LocalizationModal />
      </>
    );
  },
  errorComponent: ErrorPage,
  pendingComponent: () => null,
  validateSearch: globalSearchSchema,
  search: {
    middlewares: [
      ({ search, next }) =>
        Object.fromEntries(
          Object.entries(next(search)).filter(
            ([k]) => !searchParameterToRemoveAfterUsage.includes(k),
          ),
        ),
    ],
  },
});
